import type { BusyBlock, CalendarPerson, Slot } from "./calendarModel";
import { isHard, onDay } from "./calendarModel";
import type { GridItem } from "./TimeGrid";

/** Everyone's busy time on *days* as grid blocks; *describe* says what a block is ("Dana: Standup, 9–9:30 AM"). */
export function busyItems(
  people: CalendarPerson[],
  days: string[],
  describe: (person: CalendarPerson, block: BusyBlock) => string,
  { labels = false }: { labels?: boolean } = {},
): GridItem[] {
  return people.flatMap((person) =>
    person.busy.flatMap((block, i) =>
      days.flatMap((date) => {
        const part = onDay(block, date);
        if (!part) return [];
        return [
          {
            id: `${person.email}-${i}-${date}`,
            date,
            from: part[0],
            to: part[1],
            tone: isHard(block.status) ? ("busy" as const) : ("tentative" as const),
            title: describe(person, block),
            label: labels ? (block.subject ?? undefined) : undefined,
          },
        ];
      }),
    ),
  );
}

/** Suggested times as blocks to pick from; the picked one stands out. */
export function slotItems(
  slots: Slot[],
  picked: string | null,
  describe: (slot: Slot) => string,
  onPick: (slot: Slot) => void,
): GridItem[] {
  return slots.flatMap((slot) => {
    const date = slot.start.slice(0, 10);
    const part = onDay(slot, date);
    if (!part) return [];
    return [
      {
        id: `slot-${slot.start}`,
        date,
        from: part[0],
        to: part[1],
        tone: slot.start === picked ? ("picked" as const) : ("slot" as const),
        title: describe(slot),
        onSelect: () => onPick(slot),
      },
    ];
  });
}

/** People busy (not just tentative) during [start, end) on its day. */
export function busyDuring(people: CalendarPerson[], start: string, end: string): string[] {
  const date = start.slice(0, 10);
  const span = onDay({ start, end }, date);
  if (!span) return [];
  return people
    .filter((p) =>
      p.busy.some((b) => {
        const part = isHard(b.status) ? onDay(b, date) : null;
        return part !== null && part[0] < span[1] && part[1] > span[0];
      }),
    )
    .map((p) => p.email);
}
